import React, { useState, useEffect } from 'react';
import PageHeader from '../components/PageHeader';
import usePageMeta from '../hooks/usePageMeta';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { trackEvent } from '../lib/analytics';

const IMAGES = [
  {
    src: 'https://customer-assets-wrfwihn1.emergentagent.net/job_realotk-rebuild/artifacts/7xnamcmd_Screenshot_20260911_194420_Chrome.webp',
    alt: 'Real OTK session — bare bottom discipline',
  },
  {
    src: 'https://customer-assets-wrfwihn1.emergentagent.net/job_realotk-rebuild/artifacts/mkz73eea_Screenshot_20260911_194804_Chrome.webp',
    alt: 'OTK session — panties pulled down',
  },
  {
    src: 'https://customer-assets-wrfwihn1.emergentagent.net/job_realotk-rebuild/artifacts/gcqvelfl_20260911_195917.webp',
    alt: 'Client positioned over the knee in leggings',
  },
  {
    src: 'https://customer-assets-wrfwihn1.emergentagent.net/job_realotk-rebuild/artifacts/1kihmrx5_Screenshot_20260911_194931_Chrome.webp',
    alt: 'OTK spanking mid-session',
  },
  {
    src: 'https://customer-assets-wrfwihn1.emergentagent.net/job_realotk-rebuild/artifacts/r22jlk56_Screenshot_20260911_195125_Chrome.webp',
    alt: 'Hairbrush spanking session',
  },
];

const Gallery = () => {
  usePageMeta({
    title: 'Gallery — Real OTK Sessions',
    description: 'Photos from real disciplinary sessions with RealOTK. Names and faces are always kept private. Real spankings, real clients.',
    path: '/gallery',
  });

  const [openIndex, setOpenIndex] = useState(null);
  const isOpen = openIndex !== null;

  const close = () => setOpenIndex(null);
  const prev = () => setOpenIndex((i) => (i - 1 + IMAGES.length) % IMAGES.length);
  const next = () => setOpenIndex((i) => (i + 1) % IMAGES.length);

  useEffect(() => {
    if (openIndex === null) return;
    trackEvent('gallery_image_open', {
      image_number: openIndex + 1,
      image_name: IMAGES[openIndex].alt,
    });
  }, [openIndex]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') close();
      if (e.key === 'ArrowLeft') prev();
      if (e.key === 'ArrowRight') next();
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  return (
    <div>
      <PageHeader
        eyebrow="Session moments"
        title="Gallery"
        subtitle="A discreet look at real disciplinary sessions. Faces and identifying details are always kept private."
        image="https://images.unsplash.com/photo-1621365039903-fd09cc003898?auto=format&fit=crop&w=3840"
      />

      <section className="bg-white">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-16 md:py-20">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {IMAGES.map((img, i) => (
              <button
                key={img.src}
                type="button"
                onClick={() => setOpenIndex(i)}
                className="group relative overflow-hidden bg-neutral-100 aspect-[4/3] focus:outline-none focus:ring-2 focus:ring-neutral-900"
                aria-label={`Open image ${i + 1}`}
              >
                <img
                  src={img.src}
                  alt={img.alt}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/25 transition-colors duration-300" />
                <div className="absolute bottom-4 left-4 right-4 text-white text-xs uppercase tracking-widest opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  Click to view
                </div>
              </button>
            ))}
          </div>

          <p className="text-center text-neutral-500 text-sm mt-12 max-w-2xl mx-auto">
            All clients pictured have given consent. Faces, tattoos, and other identifying features
            are cropped, blurred, or angled away. Your privacy is my priority.
          </p>
        </div>
      </section>

      {isOpen && (
        <div
          className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4 md:p-10 animate-fadein"
          onClick={close}
        >
          <button
            type="button"
            onClick={close}
            aria-label="Close"
            className="absolute top-4 right-4 md:top-6 md:right-6 text-white/80 hover:text-white transition-colors"
          >
            <X size={32} strokeWidth={1.5} />
          </button>

          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); prev(); }}
            aria-label="Previous"
            className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 text-white/70 hover:text-white transition-colors p-2"
          >
            <ChevronLeft size={40} strokeWidth={1.5} />
          </button>

          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); next(); }}
            aria-label="Next"
            className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 text-white/70 hover:text-white transition-colors p-2"
          >
            <ChevronRight size={40} strokeWidth={1.5} />
          </button>

          <img
            src={IMAGES[openIndex].src}
            alt={IMAGES[openIndex].alt}
            onClick={(e) => e.stopPropagation()}
            className="max-h-[85vh] max-w-[92vw] object-contain shadow-2xl"
          />

          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/60 text-xs uppercase tracking-widest">
            {openIndex + 1} / {IMAGES.length}
          </div>
        </div>
      )}
    </div>
  );
};

export default Gallery;
